<template>
    <div>
        <div class="d-flex">
            <v-alert type="info" text dense>Changes will take effect after the application is restarted.</v-alert>
        </div>
        <div class="d-flex align-center">
            <secret-create-dialog v-if="canAddSecret()" :level="level" :entity-id="resolvedEntityId" @created="fetchSecrets">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn class="mr-2" color="primary" v-bind="attrs" v-on="on">
                        <v-icon left>mdi-plus</v-icon>
                        Secret
                    </v-btn>
                </template>
            </secret-create-dialog>
            <v-spacer></v-spacer>
            <v-text-field
                placeholder="Search for a secret..."
                autofocus
                v-model="search"
                hide-details
                prepend-inner-icon="mdi-magnify "
                solo
                flat
                dense
                clearable
                class="mr-2">
                <template v-slot:append>
                    <v-menu offset-y :close-on-content-click="false" v-if="isAccountManager && level !== 0">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                <v-icon left>mdi-tune-variant</v-icon>
                                <span>Filters</span>
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item>
                                <template v-slot:default>
                                    <v-switch v-model="includeDeleted" inset dense hide-details class="pa-0 ma-0">
                                        <template v-slot:label>
                                            <div class="d-flex align-center ml-3">
                                                <v-icon left small>mdi-trash-can-outline</v-icon>
                                                <span class="secondary--text">Show deleted</span>
                                            </div>
                                        </template>
                                    </v-switch>
                                </template>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-text-field>
            <v-btn @click="fetchSecrets()" icon><v-icon>refresh</v-icon></v-btn>
        </div>
        <v-data-table
            :items="secrets"
            item-key="srid"
            :headers="headers"
            :search="search"
            :item-class="isDeleted"
            :options="tableOptions"
            :loading="requestRunning === 'get'"
            :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
            class="mt-4">
            <template v-slot:top="{ pagination, options, updateOptions }">
                <v-data-footer
                    :pagination="pagination"
                    :options="options"
                    @update:options="updateOptions"
                    :itemsPerPageOptions="itemsPerPageOptions"
                    items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                    class="no-border" />
            </template>
            <template v-slot:no-data>
                <span>No secrets have been added yet. Click add new secret to create a new one.</span>
            </template>
            <template v-slot:[`item.name`]="{ item }">
                <code class="text-body-1">{{ item.name }}</code>
                <v-tooltip bottom v-if="item.overrides_secret_type">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon color="primary" small v-bind="attrs" v-on="on" class="ml-3">mdi-information-outline</v-icon>
                    </template>
                    <span>Overrides a secret from another scope</span>
                </v-tooltip>
                <v-tooltip bottom v-if="item.secret_type > level">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" class="ml-3">mdi-information-outline</v-icon>
                    </template>
                    <span>Defined for a broader scope</span>
                </v-tooltip>
            </template>
            <template v-slot:[`item.creator_name`]="{ item }">
                {{ item.last_editor_name || item.creator_name }}
            </template>
            <template v-slot:[`item.creation_timestamp`]="{ item }">
                {{ (item.last_modified_timestamp || item.creation_timestamp) | dateTimeToHuman }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <v-menu left offset-x nudge-top="10">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" text color="secondary" small>
                            <v-icon>more_horiz</v-icon>
                        </v-btn>
                    </template>
                    <v-list nav dense>
                        <copy-to-clipboard :textToCopy="item.name" buttonName="Copy name to clipboard" type="listItem" />
                        <v-list-item v-if="canModifySecret(item)" @click="openSecretDialog(item)">
                            <v-list-item-icon>
                                <v-progress-circular
                                    v-if="selectedSecret?.srid === item.srid && requestRunning === 'patch'"
                                    size="18"
                                    width="2"
                                    indeterminate
                                    color="secondary" />
                                <v-icon v-else color="secondary">mdi-pencil-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="secondary--text">Edit secret</v-list-item-title>
                        </v-list-item>
                        <v-divider v-if="canModifySecret(item)" class="my-2"></v-divider>
                        <v-list-item v-if="canModifySecret(item)" @click="openDeleteDialog(item)">
                            <v-list-item-icon>
                                <v-progress-circular
                                    v-if="selectedSecret?.srid === item.srid && requestRunning === 'delete'"
                                    size="18"
                                    width="2"
                                    indeterminate
                                    color="error" />
                                <v-icon v-else color="error">mdi-trash-can-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="error--text">Delete secret</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>

        <v-dialog v-model="secretDialog" max-width="600px" lazy-validation persistent>
            <v-card>
                <v-card-title class="flex-nowrap align-start">
                    <span v-if="selectedSecret">Edit {{ selectedSecret.name }}</span>

                    <v-spacer></v-spacer>
                    <v-btn @click="secretDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle class="caption">This environment variable will be available in all applications in this scope.</v-card-subtitle>
                <v-card-text class="mb-0">
                    <v-form v-model="secretFormIsValid" @submit.prevent="sendUpdateRequest()">
                        <v-textarea v-model="newSecretValue" outlined :rules="[rules.required]" dense hint="This field is required" autofocus>
                            <template v-slot:label>Type a value for your secret</template>
                        </v-textarea>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn
                        text
                        color="primary"
                        @click="sendUpdateRequest()"
                        :disabled="!secretFormIsValid"
                        v-if="selectedSecret"
                        :loading="requestRunning === 'patch'">
                        Edit
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import eventBus from '@/components/EventBus'
import { mapGetters, mapState } from 'vuex'

const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')
const SecretCreateDialog = () => import('@/components/SecretCreateDialog.vue')

export default {
    name: 'SecretsListing',
    components: { CopyToClipboard, SecretCreateDialog },
    props: {
        level: Number,
        entityId: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            secrets: [],
            search: '',
            selectedSecret: null,
            requestRunning: false,
            includeDeleted: false,
            secretDialog: false,
            secretFormIsValid: false,
            newSecretValue: '',
            rules: {
                required: value => !!value || 'This field is required'
            },
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            }
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'isSpaceArchived']),
        ...mapGetters('orgStore', ['isOrgManager']),
        ...mapState('userStore', ['isAccountManager']),
        headers() {
            return [
                { text: 'Name', align: 'left', value: 'name' },
                { text: 'Last change by', align: this.level === 0 ? ' d-none' : 'left', value: 'creator_name' },
                { text: 'Last change at', align: 'right', value: 'creation_timestamp' },
                { text: 'Actions', align: 'right', value: 'actions', sortable: false }
            ]
        },
        paramForLevel() {
            const paramsForLevels = {
                0: `personal`,
                2: `spaces`,
                3: `orgs`
            }
            return paramsForLevels[this.level]
        },
        actions() {
            return {
                delete() {
                    return {
                        endpoint: srid => `/secrets/${srid}`,
                        error: `There was an error deleting the secret.`
                    }
                },
                patch() {
                    return { endpoint: srid => `/secrets/${srid}`, error: `There was an error updating the secret.` }
                },
                get(level, includeDeleted) {
                    return {
                        endpoint: sid => (level === 'personal' ? `/secrets/personal` : `/${level}/${sid}/secrets?include_deleted=${includeDeleted}`),
                        error: `There was an error fetching secrets.`
                    }
                }
            }
        },
        resolvedEntityId() {
            if (this.entityId !== null && this.entityId !== undefined) {
                return this.entityId
            }
            if (this.level === 2) {
                return this.$route.params.sid
            }
            if (this.level === 3) {
                return this.$route.params.oid
            }
            return null
        }
    },
    methods: {
        openDeleteDialog(secret) {
            this.selectedSecret = secret
            this.$store.dispatch('showGlobalDialog', {
                dialogText: `Are you sure you would like to delete this secret: <em>${this.selectedSecret.name}</em>?`,
                dialogAction: ['delete', 'cancel'],
                dialogTitle: 'Confirm deletion'
            })
        },
        openSecretDialog(secret) {
            this.selectedSecret = secret
            this.newSecretValue = secret?.value || ''
            this.secretDialog = true
        },
        closeSecretDialog() {
            this.selectedSecret = null
            this.secretDialog = false
            this.newSecretValue = ''
        },
        async sendUpdateRequest() {
            await this.sendRequest('patch', this.selectedSecret.srid, { value: this.newSecretValue })
            this.selectedSecret = null
            this.secretDialog = false
            this.newSecretValue = ''
            this.fetchSecrets()
        },
        async sendDeleteRequest() {
            await this.sendRequest('delete', this.selectedSecret.srid)
            this.selectedSecret = null
            this.secretDialog = false
            this.fetchSecrets()
        },
        async sendRequest(method, id, body = {}) {
            this.requestRunning = method
            const axiosRequest = this.$axios[method]
            const level = this.paramForLevel
            const endpoint = this.actions[method](level, this.includeDeleted).endpoint(id)
            let response
            try {
                response = await axiosRequest(endpoint, body)
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: error.response?.data?.debug || this.actions[method]().error,
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.selectedSecret = null
            this.requestRunning = false
            return response
        },
        fetchSecrets() {
            const level = this.paramForLevel
            const id = this.resolvedEntityId
            if (level !== 'personal' && !id) {
                return
            }
            this.sendRequest('get', id).then(response => {
                this.secrets = response?.data || []
            })
        },
        isDeleted: item => item.delete_timestamp && 'text--disabled',
        canModifySecret(secret) {
            if (secret.delete_timestamp || secret.secret_type !== this.level) return false
            return this.canAddSecret()
        },
        canAddSecret() {
            if (this.level === 2) return this.isSpaceAdmin && !this.isSpaceArchived
            if (this.level === 3) return this.isOrgManager
            return this.level === 0
        },
        handleGlobalDialogAccept() {
            this.sendDeleteRequest()
        },
        handleSecretCreated({ level, entityId }) {
            if (Number(level) !== Number(this.level)) {
                return
            }
            if (this.resolvedEntityId && entityId && this.resolvedEntityId.toString() !== entityId.toString()) {
                return
            }
            this.fetchSecrets()
        }
    },
    created() {
        this.fetchSecrets()
        eventBus.$on('global-dialog-accept', this.handleGlobalDialogAccept)
        eventBus.$on('secret-created', this.handleSecretCreated)
    },
    beforeDestroy() {
        eventBus.$off('global-dialog-accept', this.handleGlobalDialogAccept)
        eventBus.$off('secret-created', this.handleSecretCreated)
    },
    watch: {
        includeDeleted() {
            this.fetchSecrets()
        },
        entityId() {
            this.fetchSecrets()
        }
    }
}
</script>
